/**
 * Two-phase trailing-pad math for the freshness lag bars, extracted from
 * `LagHistoryBar.tsx` so the (non-trivial) bucket-grid logic lives in one
 * tested place instead of being hand-mirrored against `App.tsx`'s
 * `padTrailingPending`.
 *
 * Phase 1 — fill to `expectedCount` if the backend returned a short payload
 *   (ingest still catching up). Trailing slots get real ISO timestamps when
 *   anchored on the last real bucket's `endAt` (required for pending-shimmer
 *   detection), or empty timestamps when there is no anchor.
 * Phase 2 — even when `expectedCount` is reached, extend past it for any slot
 *   whose expected end has passed `now - 2*windowMs` ago (the in-flight slot),
 *   but NOT for anchored (historical) windows whose last bucket sits far in
 *   the past. Capped at +3 extra trailing slots to avoid runaway tails.
 */
import type { LagWindow } from './lag-time';

/** Default expected bucket count per window (no per-suite cadence override). */
export function expectedBucketCount(windowSel: LagWindow): number {
  return windowSel === '90d' ? 90 : windowSel === '24h' ? 96 : 60;
}

export function padTrailingSlots<T>(
  buckets: readonly T[],
  expectedCount: number,
  windowMs: number,
  now: number,
  lastEndAt: string | undefined,
  makeSlot: (startMs: number, endMs: number) => T,
  makeEmptySlot: () => T,
): T[] {
  const padded: T[] = [...buckets];
  let nextSlotStart =
    lastEndAt !== undefined && lastEndAt !== ''
      ? new Date(lastEndAt).getTime()
      : Number.NaN;
  while (padded.length < expectedCount) {
    if (Number.isFinite(nextSlotStart)) {
      const slotEnd = nextSlotStart + windowMs;
      padded.push(makeSlot(nextSlotStart, slotEnd));
      nextSlotStart = slotEnd;
    } else {
      padded.push(makeEmptySlot());
    }
  }
  if (Number.isFinite(nextSlotStart)) {
    // Live windows extend toward "now" for the in-flight slot. An anchored
    // (historical) window's last bucket is far in the past — appending slots
    // toward the real clock would tack on phantom trailing no-data cells.
    const anchoredPast = now - nextSlotStart > 2 * windowMs;
    const slotsBehind = Math.floor((now - nextSlotStart) / windowMs) + 1;
    const extra = anchoredPast ? 0 : Math.min(Math.max(slotsBehind, 0), 3);
    for (let i = 0; i < extra; i += 1) {
      const slotEnd = nextSlotStart + windowMs;
      padded.push(makeSlot(nextSlotStart, slotEnd));
      nextSlotStart = slotEnd;
    }
  }
  return padded;
}
